import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Показать спиннер, заблокировать клики, выставить aria-busy */
  loading?: boolean;
  /** Иконка слева от текста. Используем react-icons или любые ReactNode */
  leftIcon?: ReactNode;
  /** Иконка справа */
  rightIcon?: ReactNode;
  /** Растянуть на 100% ширины контейнера */
  fullWidth?: boolean;
  /** Иконка без текста (квадратная кнопка). children используется как aria-label если string */
  iconOnly?: boolean;
}

/**
 * Базовая кнопка дизайн-системы.
 * Состояния: hover, focus-visible, active, disabled, loading.
 *
 * @example
 *   <Button variant="primary" leftIcon={<IoAdd />}>Создать</Button>
 *   <Button variant="ghost" size="sm" iconOnly aria-label="Удалить"><IoTrash /></Button>
 *   <Button variant="danger" loading>Удалить</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    iconOnly = false,
    disabled,
    children,
    className,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = [
    'ds-btn',
    `ds-btn--${variant}`,
    `ds-btn--${size}`,
    fullWidth ? 'ds-btn--block' : '',
    iconOnly ? 'ds-btn--icon-only' : '',
    loading ? 'is-loading' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
      {...rest}
    >
      {loading && <span className="ds-btn__spinner" aria-hidden="true" />}
      {!loading && leftIcon && <span className="ds-btn__icon" aria-hidden="true">{leftIcon}</span>}
      {children != null && <span className="ds-btn__label">{children}</span>}
      {!loading && rightIcon && <span className="ds-btn__icon" aria-hidden="true">{rightIcon}</span>}
    </button>
  );
});
